import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { decodeJwtPayload } from "../utils/jwt";
import { API } from "../api";

// Neslife Assistant floating chat button.
// - Admins always see it on every admin page (they're staff, not subject to
//   the access list).
// - Students see it across every tab of their selected course (Dashboard,
//   Videos, Doubts, FAQs, Messages, etc. — same "selectedCourseId" in
//   localStorage those pages already key off, set by courseselector.tsx)
//   once they've picked a course, and only if an admin enabled the chatbot
//   for that exact course or granted it to them individually for it. Never
//   shown before a course is picked or on the course-selection page itself.
//   See chatbotController.js / studentGetAccess.
const COURSE_PLAYER_PATH = /^\/course\/(\d+)/;

export default function ChatWidget() {
  const location = useLocation();
  const [allowed, setAllowed] = useState(false);

  const token = localStorage.getItem("token");
  const payload = token ? decodeJwtPayload(token) : null;
  const role = (payload?.role || "").toLowerCase();
  const isPublicPage = location.pathname === "/" || location.pathname === "/register";
  const isExcludedPage = location.pathname === "/student/select-course";

  // The course player URL is authoritative when open; everywhere else, fall
  // back to the student's last-selected course (same value every other
  // student page already reads from localStorage).
  const urlCourseId = COURSE_PLAYER_PATH.exec(location.pathname)?.[1];
  const storedCourseId = localStorage.getItem("selectedCourseId");
  const courseId = Number(urlCourseId || storedCourseId) || null;

  useEffect(() => {
    let cancelled = false;

    if (!token || !payload || isPublicPage || isExcludedPage) {
      setAllowed(false);
      return;
    }

    if (role === "admin") {
      setAllowed(true);
      return;
    }

    if (role === "student") {
      if (!courseId) {
        setAllowed(false);
        return;
      }
      API.get<{ enabled: boolean }>(`/student/chatbot/access?courseId=${courseId}`)
        .then((res) => { if (!cancelled) setAllowed(Boolean(res.data?.enabled)); })
        .catch(() => { if (!cancelled) setAllowed(false); });
      return () => { cancelled = true; };
    }

    setAllowed(false);
    // Re-check on every navigation so switching courses, a fresh login, and a
    // newly granted/revoked access all take effect without a full page reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, role, location.pathname, courseId]);

  useEffect(() => {
    if (!allowed) return;

    const script = document.createElement("script");
    script.src = "https://llm.familyfinance.in/widget.js";
    document.body.appendChild(script);

    return () => {
      script.remove();
      document.querySelectorAll(".myrag-btn, .myrag-panel").forEach((el) => el.remove());
      (window as any).__myragWidget = false;
    };
  }, [allowed]);

  return null;
}
